import { useState, useEffect, useRef, useMemo } from "react";
import BarraSuperior from "../barra_superior";
import AnimacionCarga from "../animacion_de_carga";
import "../../css/animacion_de_carga.css";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart,
  RadialLinearScale,
  PointElement,
  LineController,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ArcElement,
} from 'chart.js';
import {
  obtenerUsuarios,
  obtenerCalificaciones
} from "../../../functions/consultas";
import {
  crearGrafica,
  crearTitulo,
  crearGraficaPercentilesEdad
} from "../../../functions/gestion";
import { consultarSimple } from "../../../functions/crud";
import { consultarConCache } from "../../../functions/consultarConCache";

const NITS_EXCLUIDOS = [];

const SelectorCliente = ({ clientes, clienteSeleccionado, onSeleccionar }) => {
  const [busqueda, setBusqueda] = useState("");
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  const nombreSeleccionado = clienteSeleccionado
    ? clientes[clienteSeleccionado]?.nombre
    : "";

  useEffect(() => {
    setBusqueda(nombreSeleccionado || "");
  }, [nombreSeleccionado]);

  useEffect(() => {
    const handleClickFuera = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setAbierto(false);
        setBusqueda(nombreSeleccionado || "");
      }
    };
    document.addEventListener("mousedown", handleClickFuera);
    return () => document.removeEventListener("mousedown", handleClickFuera);
  }, [nombreSeleccionado]);

  const listaFiltrada = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    return Object.entries(clientes)
      .map(([nit, { nombre }]) => ({ nit, nombre }))
      .filter(({ nombre }) => !termino || nombre.toLowerCase().includes(termino))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [clientes, busqueda]);

  const seleccionar = (nit, nombre) => {
    onSeleccionar(nit);
    setBusqueda(nombre);
    setAbierto(false);
  };

  const limpiar = () => {
    onSeleccionar("");
    setBusqueda("");
    setAbierto(false);
  };

  return (
    <div ref={ref} style={{ position: "relative", minWidth: "340px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type="text"
          value={busqueda}
          placeholder="Escribir para buscar copropiedad..."
          onChange={(e) => {
            setBusqueda(e.target.value);
            setAbierto(true);
            if (!e.target.value) onSeleccionar("");
          }}
          onFocus={() => setAbierto(true)}
          style={{
            padding: "6px 12px",
            fontSize: "1em",
            width: "100%",
            border: "1px solid #b0c4de",
            borderRadius: 4,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {clienteSeleccionado && (
          <button
            onClick={limpiar}
            title="Limpiar selección"
            style={{ padding: "6px 10px", fontSize: "0.95em", cursor: "pointer", whiteSpace: "nowrap" }}
          >
            ✕ Limpiar
          </button>
        )}
      </div>

      {abierto && (
        <ul
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            maxHeight: 280,
            overflowY: "auto",
            margin: 0,
            padding: 0,
            listStyle: "none",
            backgroundColor: "#fff",
            border: "1px solid #b0c4de",
            borderRadius: 4,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            zIndex: 9999,
          }}
        >
          <li
            onClick={() => { onSeleccionar(""); setBusqueda(""); setAbierto(false); }}
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              color: "#888",
              borderBottom: "1px solid #eee",
              fontStyle: "italic",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0f8ff")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            — Todos los clientes —
          </li>

          {listaFiltrada.length > 0 ? (
            listaFiltrada.map(({ nit, nombre }) => (
              <li
                key={nit}
                onClick={() => seleccionar(nit, nombre)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  backgroundColor: nit === clienteSeleccionado ? "#ddeeff" : "",
                  fontWeight: nit === clienteSeleccionado ? "bold" : "normal",
                }}
                onMouseEnter={(e) => {
                  if (nit !== clienteSeleccionado)
                    e.currentTarget.style.backgroundColor = "#f0f8ff";
                }}
                onMouseLeave={(e) => {
                  if (nit !== clienteSeleccionado)
                    e.currentTarget.style.backgroundColor = "";
                }}
              >
                {nombre}
              </li>
            ))
          ) : (
            <li style={{ padding: "8px 12px", color: "#aaa" }}>Sin resultados</li>
          )}
        </ul>
      )}
    </div>
  );
};

const ListadoCalificaciones = () => {
  const [animacionCarga, setCargando] = useState(true);
  const [datosMostrar, setDatosMostrar] = useState(false);
  const [graficaMostrar, setGraficaMostrar] = useState(true);

  const [usuariosCrudos, setUsuariosCrudos] = useState({});
  const [calificacionesCrudas, setCalificacionesCrudas] = useState({});

  const [clientes, setClientes] = useState({});
  const [clienteSeleccionado, setClienteSeleccionado] = useState("");

  const hasFetched = useRef(false);

  const incrementar = (map, key) => key && map.set(key, (map.get(key) || 0) + 1);

  useEffect(() => {
    (async () => {
      const lista = await consultarConCache("clientes/", consultarSimple);
      if (!lista) return;

      const clientesFinal = {};
      await Promise.all(
        Object.entries(lista).map(async ([nit, auto]) => {
          const data = await consultarConCache(
            `coordi_ph_clientes/${nit}/${auto}`,
            consultarSimple
          );
          if (data?.Estado !== "RECHAZADO" && !NITS_EXCLUIDOS.includes(nit)) {
            clientesFinal[nit] = { id: auto, nombre: data.Nombre.toUpperCase() };
          }
        })
      );
      setClientes(clientesFinal);
    })();
  }, []);

  const procesarUsuarios = (usuarios, codigoClienteFiltro = null) => {
    const maps = {
      version: new Map(),
      dispositivo: new Map(),
      horaLogin: new Map(),
      mesLogin: new Map(),
      diaLogin: new Map(),
      genero: new Map(),
      rangoEdad: new Map(),
    };

    Object.values(usuarios).forEach(data => {
      if (codigoClienteFiltro && data.CodigoCliente !== codigoClienteFiltro) return;

      incrementar(maps.version, data.versionApp || "1.2.18");
      incrementar(maps.dispositivo, data.tipoDispositivo);
      incrementar(maps.genero, data.Genero);
      incrementar(maps.rangoEdad, data.RangoEdad);

      if (data.ultimoIngreso) {
        incrementar(maps.horaLogin, data.ultimoIngreso.substring(11, 13));
        incrementar(maps.diaLogin, data.ultimoIngreso.substring(0, 10));
        incrementar(maps.mesLogin, data.ultimoIngreso.substring(5, 7));
      }
    });

    return {
      version: Object.fromEntries(maps.version),
      dispositivo: Object.fromEntries(maps.dispositivo),
      horaLogin: Object.fromEntries(maps.horaLogin),
      mesLogin: Object.fromEntries(maps.mesLogin),
      diaLogin: Object.fromEntries(maps.diaLogin),
      genero: Object.fromEntries(maps.genero),
      rangoEdad: Object.fromEntries(maps.rangoEdad),
    };
  };

  const procesarCalificaciones = (calificaciones, usuariosFiltrados = null) => {
    const conteo = Object.fromEntries(
      Array.from({ length: 10 }, (_, i) => [(i + 1).toString().padStart(2, "0"), 0])
    );

    Object.entries(calificaciones).forEach(([correoKey, valor]) => {
      if (usuariosFiltrados) {
        const coincide = Object.values(usuariosFiltrados).some(u => {
          if (!u.correo) return false;
          const normalizado = u.correo.replace(/[@.]/g, "");
          return normalizado === correoKey;
        });
        if (!coincide) return;
      }

      const num = parseInt(valor);
      if (num >= 1 && num <= 10) {
        conteo[num.toString().padStart(2, "0")]++;
      }
    });

    return conteo;
  };

  const capturarDatos = async () => {
    setCargando(true);
    try {
      const [usuariosResult, calificacionesResult] = await Promise.allSettled([
        obtenerUsuarios(),
        obtenerCalificaciones()
      ]);

      if (usuariosResult.status === 'fulfilled' && usuariosResult.value)
        setUsuariosCrudos(usuariosResult.value);

      if (calificacionesResult.status === 'fulfilled' && calificacionesResult.value)
        setCalificacionesCrudas(calificacionesResult.value);

      console.log("✅ Datos obtenidos correctamente");
    } catch (error) {
      console.error("Error capturando datos:", error);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    Chart.register(
      RadialLinearScale, PointElement, LineElement, LineController, Filler,
      Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, ArcElement, ChartDataLabels
    );
  }, []);

  useEffect(() => {
    if (!hasFetched.current) {
      capturarDatos();
      hasFetched.current = true;
    }
  }, []);

  const estadisticasUsuarios = useMemo(() => {
    if (Object.keys(usuariosCrudos).length === 0) return {};

    const codigoFiltro = clienteSeleccionado || null;

    let usuariosFiltrados = null;
    if (codigoFiltro) {
      usuariosFiltrados = Object.fromEntries(
        Object.entries(usuariosCrudos).filter(
          ([, data]) => data.CodigoCliente === codigoFiltro
        )
      );
    }

    const datosUsuarios = procesarUsuarios(usuariosCrudos, codigoFiltro);
    const datosCalificaciones =
      Object.keys(calificacionesCrudas).length > 0
        ? procesarCalificaciones(calificacionesCrudas, usuariosFiltrados)
        : undefined;

    return { ...datosUsuarios, calificaciones: datosCalificaciones };
  }, [usuariosCrudos, calificacionesCrudas, clienteSeleccionado]);

  if (animacionCarga) return <AnimacionCarga />;

  return (
    <div>
      <BarraSuperior />
      <div className="BloqueModulo">

        {Object.keys(clientes).length > 0 && (
          <div style={{
            padding: 20,
            backgroundColor: "#f0f8ff",
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}>
            <label style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
              Ver estadísticas de cliente específico:
            </label>
            <SelectorCliente
              clientes={clientes}
              clienteSeleccionado={clienteSeleccionado}
              onSeleccionar={setClienteSeleccionado}
            />
            {clienteSeleccionado && (
              <span style={{ color: "#555", fontSize: "0.9em" }}>
                Mostrando datos de:{" "}
                <strong>{clientes[clienteSeleccionado]?.nombre}</strong>
              </span>
            )}
          </div>
        )}

        <div style={{ padding: '0 10px 0 0', textAlign: 'right' }}>
          <br />
          <h2>Opciones de visualización</h2>
          <br />
          <label>
            <input
              type="checkbox"
              checked={graficaMostrar}
              onChange={(e) => setGraficaMostrar(e.target.checked)}
            />
            Mostrar Gráfica de datos
          </label>
          <br />
          <label>
            <input
              type="checkbox"
              checked={datosMostrar}
              onChange={(e) => setDatosMostrar(e.target.checked)}
            />
            Mostrar Tabla de datos
          </label>
        </div>

        <div>
          <br />
          {estadisticasUsuarios.version && crearGrafica('radar', 'Versiones de la app instaladas', estadisticasUsuarios.version, 600, 1300, "texto", graficaMostrar, datosMostrar)}
          {estadisticasUsuarios.dispositivo && crearGrafica('barra', 'Tipos de dispositivo', estadisticasUsuarios.dispositivo, 600, 1300, "texto", graficaMostrar, datosMostrar)}
          {estadisticasUsuarios.genero && crearGrafica('barra', 'Género', estadisticasUsuarios.genero, 600, 1300, "texto", graficaMostrar, datosMostrar)}
          {estadisticasUsuarios.rangoEdad && crearGrafica('barra', 'Rango de Edad', estadisticasUsuarios.rangoEdad, 600, 1300, "texto", graficaMostrar, datosMostrar)}

          {estadisticasUsuarios.rangoEdad && crearGraficaPercentilesEdad(
            estadisticasUsuarios.rangoEdad,
            graficaMostrar,
            datosMostrar
          )}

          {crearTitulo('Usabilidad')}
          <br />
          {estadisticasUsuarios.mesLogin && crearGrafica('barra', 'Uso por mes', estadisticasUsuarios.mesLogin, 600, 1300, "texto", graficaMostrar, datosMostrar)}
          {estadisticasUsuarios.diaLogin && crearGrafica('barra', 'Uso por día', estadisticasUsuarios.diaLogin, 600, 1300, "texto", graficaMostrar, datosMostrar)}
          {estadisticasUsuarios.horaLogin && crearGrafica('barra', 'Uso por hora', estadisticasUsuarios.horaLogin, 600, 1300, "texto", graficaMostrar, datosMostrar)}

          {crearTitulo('Calificaciones')}
          <br />
          {estadisticasUsuarios.calificaciones && crearGrafica('barra', 'Distribución de Calificaciones (1 a 10)', estadisticasUsuarios.calificaciones, 600, 1300, "texto", graficaMostrar, datosMostrar)}
        </div>

      </div>
    </div>
  );
};

export default ListadoCalificaciones;